import gsap from 'gsap'
import {Color, DoubleSide, Group, Mesh, ShaderMaterial, Vector4} from 'three'
import {GLTFLoader} from 'three/examples/jsm/loaders/GLTFLoader.js'
import {mergeBufferGeometries} from 'three/examples/jsm/utils/BufferGeometryUtils.js'

import monaNormalGLB from '../../../../assets/images/brandedDivider/mona-normal.glb'
import monaGlanceGLB from '../../../../assets/images/brandedDivider/mona-upwardGlance.glb'
import type {BrandedDividerParams} from '../../../../components/WebGLContext/WebGLContext.types'
import fragmentShader from '../../../_shaders/brandedDivider/mona'
import vertexShader from '../../../_shaders/vertexShader'

export default class BaseMona {
  group: Group = new Group()
  params: BrandedDividerParams
  progress: Vector4 = new Vector4(0, 0, 0, 0)
  rectData: Vector4 = new Vector4(0, 0, 0, 0)
  tl: gsap.core.Timeline = gsap.timeline({paused: true})
  whiskers: {
    original: Mesh[]
    mirrored: Mesh[]
  } = {
    original: [] as Mesh[],
    mirrored: [] as Mesh[],
  }
  isLoaded: boolean = false
  isNeededToPlayTl: boolean = false
  constructor(params: BrandedDividerParams, rectData: Vector4) {
    this.params = params
    this.rectData = rectData
    this.group.visible = false
  }

  async create(group: Group, tl: gsap.core.Timeline) {
    const glslLoader = new GLTFLoader()
    const src = this.params.layout === 0 ? monaGlanceGLB : monaNormalGLB
    await glslLoader.loadAsync(src).then(glsl => {
      const eyeGroup = new Group()
      eyeGroup.name = 'EyeGroup'
      this.group.add(eyeGroup)

      glsl.scene.traverse(child => {
        if (child instanceof Mesh) {
          const geometry = child.geometry
          const material = this.createMaterial(child.name)

          const mirroredGeometry = geometry.clone()
          mirroredGeometry.scale(-1, 1, 1) // Mirror on the X-axis

          switch (child.name) {
            case 'Highlight_1':
            case 'Highlight_2':
              const highlightMesh = new Mesh(geometry, material)
              highlightMesh.position.copy(child.position)
              highlightMesh.rotation.copy(child.rotation)
              this.group.add(highlightMesh)
              highlightMesh.name = child.name

              if (this.params.layout === 1) {
                highlightMesh.position.x *= -1
                highlightMesh.scale.x *= -1
              }
              break
            case 'Whiskers_1':
            case 'Whiskers_2':
              const mirroredMesh = new Mesh(mirroredGeometry, material)
              mirroredMesh.position.copy(child.position)
              mirroredMesh.position.x *= -1 // Mirror the position on the X-axis
              mirroredMesh.rotation.copy(child.rotation)
              this.group.add(mirroredMesh)
              mirroredMesh.name = `${child.name}_mirrored`

              const originalMesh = new Mesh(geometry, material)
              originalMesh.position.copy(child.position)
              originalMesh.rotation.copy(child.rotation)
              this.group.add(originalMesh)
              originalMesh.name = `${child.name}_original`

              if (child.name === 'Whiskers_1') {
                this.whiskers.original[0] = originalMesh
                this.whiskers.mirrored[0] = mirroredMesh
              }

              if (child.name === 'Whiskers_2') {
                this.whiskers.original[1] = originalMesh
                this.whiskers.mirrored[1] = mirroredMesh
              }

              break
            case 'Eye':
            case 'Pupil':
            case 'Highlight':
              const eyeMesh = new Mesh(geometry, material)
              eyeMesh.position.copy(child.position)
              eyeMesh.rotation.copy(child.rotation)
              eyeGroup.add(eyeMesh)
              eyeMesh.name = child.name

              break
            default:
              // combine original and mirrored geometry
              const combinedGeometry = mergeBufferGeometries([geometry, mirroredGeometry])
              const mesh = new Mesh(combinedGeometry, material)

              mesh.position.copy(child.position)
              mesh.rotation.copy(child.rotation)
              this.group.add(mesh)
              mesh.name = child.name
              break
          }
        }
      })

      const mirroredEyeGroup = eyeGroup.clone()
      mirroredEyeGroup.name = 'EyeGroup_mirrored'
      mirroredEyeGroup.scale.set(-1, 1, 1)
      this.group.add(mirroredEyeGroup)

      this.createTl()
      this.isLoaded = true
    })

    group.add(this.group)
  }

  createTl() {
    if (this.isNeededToPlayTl) {
      this.tl.play()
    }
  }

  createMaterial(name: string) {
    let defines = {}
    const uniforms = {
      uColor1: {value: new Color(0x6348fa)}, // purple
      uColor2: {value: new Color(0xc149d8)}, // pink
      uRectData: {value: this.rectData},
    }

    switch (name) {
      case 'Head':
        defines = {
          HEAD: '',
        }
        break
      case 'Face':
        defines = {
          FACE: '',
        }
        uniforms.uColor1.value.set(0xdd37c3) // pink
        uniforms.uColor2.value.set(0xfed7e9) // light pink
        break
      case 'Mouth':
        defines = {
          MOUTH: '',
        }
        uniforms.uColor1.value.set(0x000000)
        break
      case 'Nose':
        defines = {
          NOSE: '',
        }
        uniforms.uColor1.value.set(0x352075)
        uniforms.uColor2.value.set(0xffffff)
        break
      case 'Eye':
      case 'Highlight':
      case 'Highlight_1':
      case 'Highlight_2':
        defines = {
          EYE: '',
        }
        uniforms.uColor1.value.set(0xf9e1f2)
        break
      case 'Pupil':
        defines = {
          PUPIL: '',
        }
        uniforms.uColor1.value.set(0x362276)
        uniforms.uColor2.value.set(0x6338df)
        break
      case 'EyeLid':
        defines = {
          EYELID: '',
        }
        uniforms.uColor1.value.set(0xb518b8)
        break
      case 'Whiskers_1':
      case 'Whiskers_2':
        defines = {
          WHISKERS: '',
        }
        uniforms.uColor1.value.set(0x513cfc) // blue
        uniforms.uColor2.value.set(0x973bfb) // light blue
        break
      case 'Ear':
        defines = {
          EAR: '',
        }
        uniforms.uColor1.value.set(0xb95efe) // purple
        uniforms.uColor2.value.set(0xff53c3) // pink
        break
    }
    return new ShaderMaterial({
      vertexShader,
      fragmentShader,
      transparent: true,
      side: DoubleSide,
      uniforms,
      defines,
    })
  }

  playTl() {
    this.isNeededToPlayTl = true
    if (this.isLoaded) {
      this.tl.play()
    }
  }

  update(scrollProgress: number) {}
}
